import { ModelInclude, ModelRequestStore, SearchCriteria, IKeyStrategy } from '../..'
import { BaseModel } from '../../../..'
import { Observable } from 'rxjs/Rx'

export class RequestCache {
  private _usedKeysByType: Map<string, Set<string>> = new Map()

  public constructor(private _keyStrategy: IKeyStrategy<BaseModel>,
                     private _modelRequestStore: ModelRequestStore<Observable<BaseModel[]>>) {}

  public get(method: string, type: string, searchCriteria: string | { [key: string]: any }, includesModel: ModelInclude) {
    const key = this._keyStrategy[method](type, searchCriteria)

    return this._modelRequestStore.getRequest(key, includesModel)
  }

  public set(method: string, type: string, searchCriteria: string | { [key: string]: any },
             includesModel: ModelInclude, requestObservable: Observable<BaseModel[]>) {
    const key = this._keyStrategy[method](type, searchCriteria)

    this._cacheObservable(type, key, includesModel, requestObservable)
  }

  public isCacheable(method: string, type: string, searchCriteria: string | { [key: string]: any },
                     includesModel: ModelInclude) {
    const searchCriteriaModel = SearchCriteria.fromCriteria(this._normalizedSearchCriteria(searchCriteria))
    return searchCriteriaModel.isEmpty() || searchCriteriaModel.isIdOnly()
  }

  public invalidateForModelsByType(method: string, model: BaseModel[] | BaseModel) {
    const models = Array.isArray(model) ? model : [model]
    const distinctModelTypes = this._keyStrategy[method](models)

    distinctModelTypes.forEach((type) => this._invalidateCacheByModelType(type))
  }

  public getCacheStrategyByModel(type: string,
                                 searchCriteria: string|{ [key: string]: any },
                                 includesModel: ModelInclude): (models: BaseModel | BaseModel[]) => void {
    const normalizedCriteria = this._normalizedSearchCriteria(searchCriteria)
    const searchCriteriaType = SearchCriteria.fromCriteria(normalizedCriteria).getSearchCriteriaType()
    switch (searchCriteriaType) {
      case 'all': return this._cacheStrategyFindBy(type, normalizedCriteria, includesModel)
      case 'id': return this._cacheStrategyFindById(type, normalizedCriteria, includesModel)
      default: return (models: BaseModel[]) => {}
    }
  }

  private _normalizedSearchCriteria(searchCriteria: string | { [key: string]: any }) {
    return typeof searchCriteria !== 'object' ? {id: searchCriteria} : searchCriteria
  }

  private _cacheResultArray(type: string, key: string, includesModel: ModelInclude, data: BaseModel[]) {
    this._cacheObservable(type, key, includesModel, Observable.of(data))
  }

  private _cacheObservable(type: string, key: string, includesModel: ModelInclude, data: Observable<BaseModel[]>) {
    this._modelRequestStore.setRequest(key, includesModel, data)
    const keys = this._usedKeysByType.get(type) || new Set()
    keys.add(key)
    this._usedKeysByType.set(type, keys)
  }

  private _cacheStrategyFindBy(type: string, searchCriteria: { [key: string]: any }, includesModel: ModelInclude) {
    return (models: BaseModel[]) => {
      const key = this._keyStrategy.findBy(type, searchCriteria)
      this._cacheResultArray(type, key, includesModel, models)

      this._cacheDescendantsAndSelfOfTypeById(type, includesModel, models)
    }

  }

  private _cacheStrategyFindById(type: string, _searchCriteria: { [key: string]: any }, includesModel: ModelInclude) {
    // Models found by POST endpoint; payload = {id: 123} will be returned as BaseModel[]
    // Models found by GET endpoint/123 will be returned as BaseModel
    // Therefore, both need to be accepted (and will be unified as BaseModel[] here)
    return (model: BaseModel|BaseModel[]) => {
      const singleModel = Array.isArray(model) ? model[0] : model
      this._cacheDescendantsAndSelfOfTypeById(type, includesModel, [singleModel])
    }
  }

  private _invalidateCacheByModelType(type: string) {
    const usedKeys = this._usedKeysByType.get(type) || new Set()
    usedKeys.forEach((usedKey) => this._modelRequestStore.deleteRequest(usedKey))
    usedKeys.clear()
    this._usedKeysByType.delete(type)
  }

  private _cacheDescendantsAndSelfOfTypeById(type: string, includesModel: ModelInclude, models: object[]): void {
    if (typeof models !== 'object' || models === null) { return }
    const normalizedModels = models

    const processedModels = new ModelRequestStore()
    const isProcessed = (key, inclModel) => !! processedModels.getRequest(key, inclModel)

    const isValidModel = (obj) => obj instanceof BaseModel && obj.type === type
    const cacheModel = (newModel) => {
      const findByIdKey = this._keyStrategy.findById(type, newModel.id, propertyScopedInclude.toArray())
      if (isProcessed(findByIdKey, propertyScopedInclude)) { return false }

      const dataToCache = [newModel]
      processedModels.setRequest(findByIdKey, includesModel, dataToCache)
      this._cacheResultArray(type, findByIdKey, propertyScopedInclude, dataToCache)
      if (!propertyScopedInclude.isEmpty()) {
        unprocessedModels.push({include: propertyScopedInclude, models: dataToCache})
      }
      return true
    }
    const unprocessedModels = []
    let propertyScopedInclude: ModelInclude = includesModel
    let currentProperties: string[]
    let currentPropertyValue: any
    let current: {include: ModelInclude, models: any[]}

    normalizedModels.forEach((mdl) => {if (isValidModel(mdl)) { cacheModel(mdl) }})
    while (unprocessedModels.length > 0) {
      current = unprocessedModels.pop()
      for (const currentModel of current.models) {
        currentProperties = Object.keys(currentModel)

        for (const currentPropertyName of currentProperties) {
          currentPropertyValue = currentModel[currentPropertyName]
          if (!current.include.hasIncludedProperty(currentPropertyName) ||
            typeof currentPropertyValue !== 'object' || currentPropertyValue === null) {
            continue
          }

          propertyScopedInclude = current.include.scopeTo(currentPropertyName)
          if (isValidModel(currentPropertyValue)) {
            cacheModel(currentPropertyValue)
          } else if (Array.isArray(currentPropertyValue)) {
            const modelsToProcess = currentPropertyValue.filter((m) => isValidModel(m) && cacheModel(m))
            unprocessedModels.push({include: propertyScopedInclude, models: modelsToProcess})
          }
        }
      }
    }
  }
}



// WEBPACK FOOTER //
// ./node_modules/@mprep/shared/lib/model-repository/backends/memory-backend/request-cache/request-cache.ts